import { Injectable } from '@angular/core'
import { BehaviorSubject, Subject } from 'rxjs'
import { APP_CONFIG } from '../config/environment'
import { AuthService } from './auth.service'

export type OutboundSyncSource = 'all' | 'atomic' | 'bulk'
export type OutboundSyncState = 'all' | 'queued' | 'processing'

export interface OutboundSyncItem {
  source: Exclude<OutboundSyncSource, 'all'>
  state: Exclude<OutboundSyncState, 'all'>
  syncId: string
  entityReference: string | null
  entityReferences: string[]
  /** A sync may apply to several CDC entities; never collapse this to one label. */
  entityTypes: string[]
  entityCounts: Record<string, number>
  queuedAt: string | null
  nextActionAt: string | null
  attemptCount: number | null
  lastError: string | null
}

export interface OutboundSyncPage {
  items: OutboundSyncItem[]
  nextCursors: { atomic: string | null; bulk: string | null }
  summary: Record<string, { queued: number; processing: number; total: number }> | null
}

export interface OutboundSyncFilters {
  source: OutboundSyncSource
  state: OutboundSyncState
}

export interface OutboundSyncStatus {
  queued: number | null
  processing: number | null
  live: 'connecting' | 'connected' | 'unavailable'
  entityTypes: Array<{ type: string; queued: number; processing: number }>
}

@Injectable({ providedIn: 'root' })
export class OutboundSyncsService {
  private readonly statusSubject = new BehaviorSubject<OutboundSyncStatus>({
    queued: null, processing: null, live: 'connecting', entityTypes: [],
  })
  readonly status$ = this.statusSubject.asObservable()
  private readonly changesSubject = new Subject<void>()
  /** Emitted after the shared socket reports that outbound-sync data changed. */
  readonly changes$ = this.changesSubject.asObservable()
  private readonly uploadedFilesChangesSubject = new Subject<void>()
  /** Emitted when the backend has created or changed an uploaded-file record. */
  readonly uploadedFilesChanges$ = this.uploadedFilesChangesSubject.asObservable()
  private readonly repaymentExportsChangesSubject = new Subject<void>()
  /** Emitted when a durable repayment-export job changes. */
  readonly repaymentExportsChanges$ = this.repaymentExportsChangesSubject.asObservable()
  private statusSocket: WebSocket | null = null
  private statusReconnectTimer: ReturnType<typeof setTimeout> | null = null
  private statusReconnectAttempt = 0
  private statusMonitorStarted = false
  private statusRequest: AbortController | null = null
  private statusRefreshTimer: ReturnType<typeof setTimeout> | null = null
  private statusRefreshInFlight = false
  private statusRefreshQueued = false

  constructor(private readonly authService: AuthService) {}

  isConfigured(): boolean {
    return Boolean(APP_CONFIG.outboundSyncsWebsocketUrl)
  }

  async list(
    filters: OutboundSyncFilters,
    cursors?: { atomic: string | null; bulk: string | null },
    signal?: AbortSignal,
    includeSummary = false,
  ): Promise<OutboundSyncPage> {
    if (!APP_CONFIG.outboundSyncsApiUrl) {
      throw new Error('Outbound syncs API is not configured.')
    }
    const url = new URL(APP_CONFIG.outboundSyncsApiUrl, window.location.origin)
    url.searchParams.set('source', filters.source)
    url.searchParams.set('state', filters.state)
    url.searchParams.set('limit', '1000')
    if (includeSummary) url.searchParams.set('includeSummary', 'true')
    if (cursors?.atomic) url.searchParams.set('atomicCursor', cursors.atomic)
    if (cursors?.bulk) url.searchParams.set('bulkCursor', cursors.bulk)

    const token = await this.authService.getAzureAccessToken()
    const response = await fetch(url.toString(), {
      signal,
      headers: { Authorization: `Bearer ${token}` },
    })
    const payload = await this.readJson(response)
    if (!response.ok) {
      throw new Error(this.errorMessage(payload) ?? 'Failed to load pending outbound syncs.')
    }
    return this.toPage(payload)
  }

  async websocketUrl(): Promise<string> {
    if (!APP_CONFIG.outboundSyncsWebsocketUrl) {
      throw new Error('Outbound syncs live updates are not configured.')
    }
    const token = await this.authService.getAzureAccessToken()
    const separator = APP_CONFIG.outboundSyncsWebsocketUrl.includes('?') ? '&' : '?'
    return `${APP_CONFIG.outboundSyncsWebsocketUrl}${separator}access_token=${encodeURIComponent(token)}`
  }

  /** Keeps the app-bar snapshot current even when the Pending Syncs tab is closed. */
  startStatusMonitor(): void {
    if (this.statusMonitorStarted) return
    this.statusMonitorStarted = true
    if (!this.isConfigured()) {
      this.updateStatus({ live: 'unavailable' })
      return
    }
    if (APP_CONFIG.outboundSyncsApiUrl) this.scheduleStatusRefresh()
    void this.connectStatusSocket()
  }

  stopStatusMonitor(): void {
    this.statusMonitorStarted = false
    this.statusRequest?.abort()
    this.statusRequest = null
    if (this.statusRefreshTimer) clearTimeout(this.statusRefreshTimer)
    this.statusRefreshTimer = null
    this.statusRefreshQueued = false
    if (this.statusReconnectTimer) clearTimeout(this.statusReconnectTimer)
    this.statusReconnectTimer = null
    this.statusSocket?.close()
    this.statusSocket = null
  }

  private async refreshStatus(): Promise<void> {
    if (this.statusRefreshInFlight) {
      this.statusRefreshQueued = true
      return
    }
    this.statusRefreshInFlight = true
    const request = this.statusRequest = new AbortController()
    try {
      const page = await this.list({ source: 'all', state: 'all' }, undefined, request.signal, true)
      if (this.statusMonitorStarted && !request.signal.aborted) {
        const summary = page.summary ?? {}
        const entityTypes = Object.entries(summary)
          .map(([type, count]) => ({ type, queued: count.queued, processing: count.processing }))
          .filter((count) => count.queued > 0 || count.processing > 0)
          .sort((a, b) => this.entityTypeOrder(a.type) - this.entityTypeOrder(b.type) || a.type.localeCompare(b.type))
        this.updateStatus({
          queued: entityTypes.reduce((total, count) => total + count.queued, 0),
          processing: entityTypes.reduce((total, count) => total + count.processing, 0),
          entityTypes,
        })
      }
    } catch (error) {
      if (!request.signal.aborted) this.updateStatus({ live: 'unavailable' })
    } finally {
      this.statusRefreshInFlight = false
      if (this.statusRefreshQueued && this.statusMonitorStarted) {
        this.statusRefreshQueued = false
        this.scheduleStatusRefresh(500)
      }
    }
  }

  /** Coalesce bursts of WebSocket change events into one status request. */
  private scheduleStatusRefresh(delay = 0): void {
    if (!this.statusMonitorStarted) return
    if (this.statusRefreshInFlight) {
      this.statusRefreshQueued = true
      return
    }
    if (this.statusRefreshTimer) return
    this.statusRefreshTimer = setTimeout(() => {
      this.statusRefreshTimer = null
      void this.refreshStatus()
    }, delay)
  }

  private async connectStatusSocket(): Promise<void> {
    if (!this.statusMonitorStarted) return
    this.updateStatus({ live: 'connecting' })
    try {
      const socket = this.statusSocket = new WebSocket(await this.websocketUrl())
      socket.onopen = () => {
        this.statusReconnectAttempt = 0
        this.updateStatus({ live: 'connected' })
      }
      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(String(event.data)) as { type?: string }
          if (message.type === 'uploaded-files.changed') {
            this.uploadedFilesChangesSubject.next()
          } else if (message.type === 'repayment-exports.changed') {
            this.repaymentExportsChangesSubject.next()
          } else if (message.type === 'outbound-syncs.changed') {
            this.changesSubject.next()
            if (APP_CONFIG.outboundSyncsApiUrl) this.scheduleStatusRefresh(250)
          }
        } catch { /* Ignore non-operational socket messages. */ }
      }
      socket.onerror = () => this.updateStatus({ live: 'unavailable' })
      socket.onclose = () => this.scheduleStatusReconnect()
    } catch {
      this.updateStatus({ live: 'unavailable' })
      this.scheduleStatusReconnect()
    }
  }

  private scheduleStatusReconnect(): void {
    if (!this.statusMonitorStarted || this.statusReconnectTimer) return
    this.updateStatus({ live: 'unavailable' })
    const delay = Math.min(30_000, 1_000 * 2 ** this.statusReconnectAttempt++)
    this.statusReconnectTimer = setTimeout(() => {
      this.statusReconnectTimer = null
      void this.connectStatusSocket()
    }, delay)
  }

  private updateStatus(update: Partial<OutboundSyncStatus>): void {
    this.statusSubject.next({ ...this.statusSubject.value, ...update })
  }

  private async readJson(response: Response): Promise<unknown> {
    const body = await response.text()
    if (!body) return null
    try { return JSON.parse(body) } catch { return null }
  }

  private errorMessage(value: unknown): string | null {
    if (!value || typeof value !== 'object') return null
    const error = (value as Record<string, unknown>).error
    return typeof error === 'string' ? error : null
  }

  private toPage(value: unknown): OutboundSyncPage {
    if (!value || typeof value !== 'object') throw new Error('Outbound syncs response was invalid.')
    const payload = value as Record<string, unknown>
    if (!Array.isArray(payload.items)) throw new Error('Outbound syncs response was invalid.')
    const cursors = payload.nextCursors && typeof payload.nextCursors === 'object'
      ? payload.nextCursors as Record<string, unknown> : {}
    return {
      items: payload.items.map((item) => this.toItem(item)).filter((item): item is OutboundSyncItem => item !== null),
      nextCursors: { atomic: this.stringOrNull(cursors.atomic), bulk: this.stringOrNull(cursors.bulk) },
      summary: this.toSummary(payload.summary),
    }
  }

  private toItem(value: unknown): OutboundSyncItem | null {
    if (!value || typeof value !== 'object') return null
    const item = value as Record<string, unknown>
    const source = item.source
    const state = item.state
    if ((source !== 'atomic' && source !== 'bulk') || (state !== 'queued' && state !== 'processing')) return null
    const syncId = this.firstString(item, ['syncId', 'sync_id', 'id'])
    if (!syncId) return null
    return {
      source,
      state,
      syncId,
      entityReference: this.firstString(item, ['merchantEntityReference', 'merchant_entity_reference', 'entityReference', 'entity_reference', 'merchantId', 'merchant_id']),
      entityReferences: this.stringList(item.entityRefs ?? item.entity_refs ?? item.entityReferences ?? item.entity_references),
      entityTypes: this.entityTypes(item, source),
      entityCounts: this.entityCounts(item.entityCounts ?? item.entity_counts),
      queuedAt: this.firstString(item, ['queuedAt', 'queued_at']),
      nextActionAt: this.firstString(item, ['nextActionAt', 'next_action_at']),
      attemptCount: typeof item.attemptCount === 'number' ? item.attemptCount : typeof item.attempt_count === 'number' ? item.attempt_count : null,
      lastError: this.firstString(item, ['lastError', 'last_error']),
    }
  }

  private firstString(item: Record<string, unknown>, keys: string[]): string | null {
    for (const key of keys) if (typeof item[key] === 'string') return item[key] as string
    return null
  }
  private entityTypes(item: Record<string, unknown>, source: 'atomic' | 'bulk'): string[] {
    // Bulk workers only process repayments. Atomic rows may expose a full list when
    // the API includes CDC metadata; older API rows expose only the first entityType.
    if (source === 'bulk') return ['repayment']
    const candidates = [item.entityTypes, item.entity_types, item.cdcEntityTypes, item.cdc_entity_types, item.entityType, item.entity_type]
    const types = candidates.flatMap((value) => this.toEntityTypeList(value))
    return [...new Set(types.length ? types : ['unknown'])]
  }
  private entityCounts(value: unknown): Record<string, number> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
    return Object.fromEntries(Object.entries(value as Record<string, unknown>)
      .filter(([, count]) => typeof count === 'number')
      .map(([type, count]) => [this.normalizeEntityType(type), Number(count)])) as Record<string, number>
  }
  private stringList(value: unknown): string[] {
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
  }
  private toSummary(value: unknown): Record<string, { queued: number; processing: number; total: number }> | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null
    return Object.fromEntries(Object.entries(value as Record<string, unknown>)
      .filter(([, count]) => count && typeof count === 'object' && !Array.isArray(count))
      .map(([type, count]) => {
        const item = count as Record<string, unknown>
        const queued = typeof item.queued === 'number' ? item.queued : 0
        const processing = typeof item.processing === 'number' ? item.processing : 0
        const total = typeof item.total === 'number' ? item.total : queued + processing
        return [this.normalizeEntityType(type), { queued, processing, total }]
      }))
  }
  private toEntityTypeList(value: unknown): string[] {
    if (Array.isArray(value)) return value.flatMap((item) => this.toEntityTypeList(item))
    if (typeof value === 'string') return value.split(/[,|]/).map((type) => this.normalizeEntityType(type)).filter(Boolean)
    return []
  }
  private normalizeEntityType(value: string): string {
    const type = value.trim().toLowerCase().replace(/[\s-]+/g, '_').replace(/_?s$/, '')
    return type === 'cashadvance' ? 'cash_advance' : type
  }
  private entityTypeOrder(type: string): number {
    return ['repayment', 'merchant', 'terminal', 'cash_advance', 'unknown'].indexOf(type) + 1 || 99
  }
  private stringOrNull(value: unknown): string | null { return typeof value === 'string' ? value : null }
}
